import { API_URL } from 'env';
import { mixLogOut } from '../services/mixin';

export interface RoleItem {
    id: number;
    name: string;
    description?: string | null;
    permissions?: string[] | null;
    module_codes?: string[] | null;
    user_count?: number;
    users_count?: number;
    status?: 0 | 1;
    created_at?: string;
    updated_at?: string;
}

export interface RoleListResponse {
    status: boolean;
    data:
        | {
              total?: number;
              current_page?: number;
              items?: RoleItem[];
          }
        | RoleItem[]
        | null;
    msg?: string;
}

export interface RoleMutationResponse {
    status: boolean;
    data?: RoleItem | null;
    msg?: string;
}

export interface RolePayload {
    name: string;
    description?: string;
    permissions?: string[];
    module_codes?: string[];
}

const BASE = `${API_URL}/api/v1/be/roles`;

function authHeaders(): HeadersInit {
    const token = localStorage.getItem('token') || '';
    return {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
}

export async function getRoles(params?: {
    search?: string;
    take?: number;
    sort?: string;
}): Promise<RoleListResponse> {
    try {
        const query = new URLSearchParams();
        if (params?.search) query.append('search', params.search);
        if (params?.take) query.append('take', String(params.take));
        if (params?.sort) query.append('sort', params.sort);
        const qs = query.toString();

        const res = await fetch(`${BASE}${qs ? `?${qs}` : ''}`, {
            method: 'GET',
            headers: authHeaders(),
        });

        if (!res.ok) {
            if (res.status === 401) mixLogOut();
            throw new Error(`HTTP error! status: ${res.status}`);
        }

        return (await res.json()) as RoleListResponse;
    } catch (error) {
        console.error('Failed to fetch roles:', error);
        return { status: false, data: null };
    }
}

export async function createRole(payload: RolePayload): Promise<RoleMutationResponse> {
    try {
        const res = await fetch(BASE, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            if (res.status === 401) mixLogOut();
            const errBody = await res.json().catch(() => null);
            return {
                status: false,
                msg: errBody?.msg || `HTTP error! status: ${res.status}`,
            };
        }

        return (await res.json()) as RoleMutationResponse;
    } catch (error) {
        console.error('Failed to create role:', error);
        return { status: false, msg: 'Network error' };
    }
}

export async function updateRole(
    id: number,
    payload: RolePayload
): Promise<RoleMutationResponse> {
    try {
        const res = await fetch(`${BASE}/${id}`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            if (res.status === 401) mixLogOut();
            const errBody = await res.json().catch(() => null);
            return {
                status: false,
                msg: errBody?.msg || `HTTP error! status: ${res.status}`,
            };
        }

        return (await res.json()) as RoleMutationResponse;
    } catch (error) {
        console.error('Failed to update role:', error);
        return { status: false, msg: 'Network error' };
    }
}

export async function deleteRole(id: number): Promise<RoleMutationResponse> {
    try {
        const res = await fetch(`${BASE}/${id}`, {
            method: 'DELETE',
            headers: authHeaders(),
        });

        if (!res.ok) {
            if (res.status === 401) mixLogOut();
            const errBody = await res.json().catch(() => null);
            return {
                status: false,
                msg: errBody?.msg || `HTTP error! status: ${res.status}`,
            };
        }

        return (await res.json().catch(() => ({ status: true }))) as RoleMutationResponse;
    } catch (error) {
        console.error('Failed to delete role:', error);
        return { status: false, msg: 'Network error' };
    }
}
